import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { Trip, ThemeClasses } from "../types";
import {
  getStatusToneForTrip,
  getStatusStyle,
  getStatusLabel,
  getPriorityTone,
  getPriorityLabel,
  formatTripDate,
  tripSubtitle,
} from "../utils";
import { Badge, Button } from "../../../../../../components/ui";
import { useDisableCRUD } from "../../../../../../store/admin/companiesStore";
import { CRUDTooltip } from "../../../../../../components/base/CRUDTooltip";
import { ACCESS_NODES } from "../../../../../../config/access/nodes";
import { useCanWrite } from "../../../../../../hooks/useCanWrite";
import { useOpenChat } from "../../../../../../pages/support/hooks/useOpenChat";
import { activeThemeId } from "../../../../../../config/theme";

const isChatEnabled = activeThemeId === "wayak";

interface TripListRowProps {
  trip: Trip;
  themeClasses: ThemeClasses;
  onEdit: (trip: Trip) => void;
  onDetails: (trip: Trip) => void;
  onAssignments: (trip: Trip) => void;
  onDelete: (trip: Trip) => void;
  onOpenChatModal: (trip: Trip) => void;
  /** Cotizador de costos operativos; ausente cuando la app no está habilitada. */
  onQuote?: (trip: Trip) => void;
}

export function TripListRow({
  trip,
  themeClasses,
  onEdit,
  onDetails,
  onAssignments,
  onDelete,
  onOpenChatModal,
  onQuote,
}: TripListRowProps) {
  const { t } = useTranslation("adminSections");
  const canWrite = useCanWrite(ACCESS_NODES.adminTrips);
  const disableCRUD = useDisableCRUD();
  const { tryOpenExistingTripChat, isReady } = useOpenChat();
  const [isOpeningChat, setIsOpeningChat] = useState(false);

  const assignmentsCount = trip.assignments?.length || 0;
  const activeAssignments =
    trip.assignments?.filter(
      (a) => a.status === "active" || a.status === "pending",
    ).length || 0;

  const assignmentsLabel = `${activeAssignments}/${assignmentsCount} ${t("trips.assignments.devices")}`;
  const createdLabel = formatTripDate(trip.createdAt);

  const handleOpenChat = async () => {
    if (!isReady || isOpeningChat) return;
    setIsOpeningChat(true);
    try {
      const opened = await tryOpenExistingTripChat(trip.id);
      if (!opened) {
        onOpenChatModal(trip);
      }
    } finally {
      setIsOpeningChat(false);
    }
  };

  return (
    <div
      className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 hover:shadow-lg transition-all`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center space-x-4 min-w-0">
          <div className="w-10 h-10 bg-info/15 text-info rounded-xl flex items-center justify-center flex-shrink-0">
            <i className="ri-route-line text-lg"></i>
          </div>
          <div className="min-w-0">
            <h3 className={`font-semibold ${themeClasses.text} truncate`}>
              {trip.title || t("trips.untitled")}
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              <p className={`text-sm ${themeClasses.textSecondary} truncate`}>
                {tripSubtitle(trip)}
              </p>
              {trip.status?.name && (
                <Badge
                  tone={getStatusToneForTrip(trip)}
                  style={getStatusStyle(trip)}
                  className="whitespace-nowrap sm:hidden"
                >
                  {getStatusLabel(trip, t)}
                </Badge>
              )}
            </div>

            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-content-secondary md:hidden">
              <button
                type="button"
                onClick={() => onAssignments(trip)}
                title={t("trips.assignments.open")}
                className="text-primary-500 hover:underline"
              >
                {assignmentsLabel}
              </button>
              <span>
                {t("trips.fields.createdAt")}: {createdLabel}
              </span>
              {trip.description && (
                <span className="w-full truncate">{trip.description}</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between sm:justify-end gap-x-4 gap-y-2 sm:gap-6 flex-shrink-0">
          <div className="hidden md:block text-center">
            <p className={`text-sm ${themeClasses.textSecondary}`}>
              {t("trips.assignments.title")}
            </p>
            <button
              type="button"
              onClick={() => onAssignments(trip)}
              title={t("trips.assignments.open")}
              className="text-xs font-bold text-primary-500 hover:underline"
            >
              {assignmentsLabel}
            </button>
          </div>
          {trip.description && (
            <div className="hidden md:block text-center">
              <p className={`text-sm ${themeClasses.textSecondary}`}>
                {t("trips.fields.description")}
              </p>
              <p
                className={`max-w-[200px] truncate text-xs font-bold ${themeClasses.text}`}
                title={trip.description}
              >
                {trip.description}
              </p>
            </div>
          )}
          <div className="hidden md:block text-center">
            <p className={`text-sm ${themeClasses.textSecondary}`}>
              {t("trips.fields.createdAt")}
            </p>
            <p className={`text-xs font-bold ${themeClasses.text}`}>
              {createdLabel}
            </p>
          </div>
          {trip.priority && (
            <Badge
              tone={getPriorityTone(trip.priority)}
              className="whitespace-nowrap flex-shrink-0"
            >
              {getPriorityLabel(trip.priority, t)}
            </Badge>
          )}
          {trip.status?.name && (
            <Badge
              tone={getStatusToneForTrip(trip)}
              style={getStatusStyle(trip)}
              className="whitespace-nowrap flex-shrink-0 max-sm:hidden"

            >
              {getStatusLabel(trip, t)}
            </Badge>
          )}
          <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start sm:flex-shrink-0">
            {isChatEnabled && (
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                loading={isOpeningChat}
                icon={<i className="ri-chat-3-line" />}
                onClick={handleOpenChat}
                disabled={!isReady || isOpeningChat}
                title={t("trips.actions.openChat", "Abrir chat del viaje")}
              />
            )}
            {canWrite && (
              <CRUDTooltip>
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  onClick={() => onEdit(trip)}
                  disabled={disableCRUD}
                  title={t("trips.actions.edit")}
                  icon={<i className="ri-pencil-line" />}
                />
              </CRUDTooltip>
            )}
            <Button
              size="sm"
              iconOnly
              onClick={() => onDetails(trip)}
              title={t("trips.actions.details")}
              icon={<i className="ri-eye-line" />}
            />
            {onQuote && (
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                onClick={() => onQuote(trip)}
                disabled={disableCRUD}
                title={t("trips.actions.quote")}
                icon={<i className="ri-calculator-line" />}
              />
            )}
            {canWrite && (
              <CRUDTooltip>
                <Button
                  variant="dangerSoft"
                  size="sm"
                  iconOnly
                  onClick={() => onDelete(trip)}
                  disabled={disableCRUD}
                  title={t("trips.actions.delete")}
                  icon={<i className="ri-delete-bin-line" />}
                />
              </CRUDTooltip>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
